import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Globe, Clock, DollarSign, Shield, Award, Handshake } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SkylineOverlay from "@/components/brand/SkylineOverlay";

const values = [
  {
    icon: Globe,
    title: "Bilingual Support",
    description:
      "Our team speaks fluent English and Arabic, ensuring crystal-clear communication with your clients and stakeholders.",
  },
  {
    icon: Clock,
    title: "Time Zone Aligned",
    description:
      "We operate during US business hours so your remote team is available when you need them , no delays, no waiting.",
  },
  {
    icon: DollarSign,
    title: "Cost Effective",
    description:
      "Access highly skilled professionals at a fraction of the cost of domestic hiring, without compromising quality.",
  },
  {
    icon: Shield,
    title: "Reliable & Secure",
    description:
      "We follow strict data handling protocols and NDAs to protect your business information at all times.",
  },
  {
    icon: Award,
    title: "Top-Tier Talent",
    description:
      "Every team member is rigorously vetted and trained to deliver the professionalism your US clients expect.",
  },
  {
    icon: Handshake,
    title: "Dedicated Partnership",
    description:
      "We don't just provide services , we become an extension of your team, invested in your success.",
  },
];

const About = () => {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About Elevate Global Solutions",
    url: "https://www.elevateglobalsolutions.net/about",
    description: "Egyptian outsourcing company providing cold callers, closers, and virtual assistants to US businesses.",
    mainEntity: { "@id": "https://www.elevateglobalsolutions.net/#organization" },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "About", item: "https://www.elevateglobalsolutions.net/about" },
    ],
  };
  return (
    <>
      <Helmet>
        <title>About Us | Elevate Global Solutions</title>
        <meta name="description" content="Elevate Global Solutions connects Egyptian cold callers, closers, and virtual assistants with US businesses. Bilingual talent, US hours." />
        <meta property="og:title" content="About Us , Egyptian Outsourcing & Cold Calling Experts" />
        <meta property="og:description" content="Egyptian outsourcing company providing cold callers, closers, and virtual assistants to US businesses." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/about" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="About Us | Elevate Global Solutions" />
        <meta name="twitter:description" content="Egyptian outsourcing experts connecting cold callers, closers and VAs with US businesses." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/about" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

    <div>
      {/* Page Header */}
      <section className="relative overflow-hidden bg-primary section-padding py-16 md:py-24">
        <div className="container-narrow relative z-10 text-center">
          <h1 className="text-4xl font-extrabold text-primary-foreground md:text-5xl">
            About Elevate Global Solutions, Egyptian Outsourcing Experts
          </h1>
          <p className="mt-4 text-lg text-primary-foreground/80">
            Egyptian outsourcing experts connecting cold callers, closers & virtual assistants with American businesses
          </p>
        </div>
        <SkylineOverlay opacityClass="opacity-[0.07]" />
      </section>

      {/* Story */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Our Mission</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Elevate Global Solutions was founded with a clear vision: to bridge the gap between Egypt's exceptional 
              workforce and the growing demand for cold calling, lead generation, appointment setting, and outsourcing services in the United States.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              We believe that geography should never limit access to talent. Our Egyptian cold callers, closers, and virtual assistants 
              combine bilingual expertise and cost efficiency to deliver an unmatched value proposition for American 
              businesses looking to scale their sales operations.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-accent/50">
        <div className="container-narrow">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">
              Why Choose Elevate Global
            </h2>
            <p className="mt-3 text-muted-foreground">
              Six reasons businesses trust us with their remote operations
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {values.map((value) => (
              <div
                key={value.title}
                className="rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:border-primary hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-primary">
                  <value.icon size={24} />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{value.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold text-foreground">Let's Work Together</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Discover how Elevate Global Solutions can transform your business operations with premium remote talent.
          </p>
          <Link to="/contact" className="mt-8 inline-block">
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
